import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@r/api/auth.tsx';
import { useGetWorkspaceConfiguration } from '@r/api/generated/client.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { isAdminOrHigher } from '@r/utils/roles.ts';

/*
 * Workspace courant et droits sur ses configurations DNS et e-mail (ROLE_ADMIN et ROLE_FIDDY_ADMIN).
 */
export const useWorkspaceConfiguration = () => {
  const user = useUser().data;
  const { workspace } = UseWorkspace(user);
  const activeWorkspace = getActiveWorkspaceUniversal(user?.visibleWorkspaces ?? []);

  return {
    workspace,
    workspaceId: workspace?.id,
    canManage: isAdminOrHigher(activeWorkspace?.maxRole),
    isMisconfigured: workspace?.status === 'misconfigured'
  };
};

/*
 * Configurations DNS et e-mail lues en direct chez Vercel et Brevo, partagées par la page « Informations
 * programme » et les pages d'authentification. Gardées en cache pour ne pas rappeler l'API à chaque visite.
 */
export const useWorkspaceConfigurationQuery = (enabled = true) => {
  const queryClient = useQueryClient();
  const { workspace, workspaceId } = useWorkspaceConfiguration();

  const query = useGetWorkspaceConfiguration(workspaceId, {
    query: {
      enabled: enabled && Boolean(workspaceId),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false
    }
  });

  // L'API réaligne le statut du workspace sur les configurations : on rafraîchit le workspace s'il a changé
  const configurationStatus = query.data?.status;
  useEffect(() => {
    if (configurationStatus && workspace?.status && configurationStatus !== workspace.status) {
      void queryClient.invalidateQueries({ queryKey: ['workspace'] });
    }
  }, [configurationStatus, workspace?.status, queryClient]);

  return {
    data: query.data,
    isError: query.isError,
    // La requête attend l'id du workspace avant de partir : tant qu'elle est voulue et sans données, on charge
    isLoading: enabled && query.isPending
  };
};
